import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { stateKeys } from '../../constants';
import { useStateControllers } from '../../stateManager';

const noop = () => {};
const emptyArray = [];
const key = stateKeys.selectedRowKeys;

const Context = createContext();

export const useRowSelectionHandlers = () => useContext(Context);

export const RowSelectionProvider = ({ children, ...props }) => {
  const { value, onChange = noop, defaultValue } = props ?? {};

  const isControlledRef = useRef(Object.hasOwn(props, 'value'));

  const { setValue, getValue } = useStateControllers();

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const selectHandler = useCallback(
    ({ key, selected }) => {
      const current = new Set(
        getValue(stateKeys.selectedRowKeys) ?? emptyArray
      );

      selected ? current.add(key) : current.delete(key);
      const next = Array.from(current);

      onChangeRef.current({
        value: next,
        valueSet: current,
        selected,
        targetKeysSet: new Set([key]),
      });

      if (!isControlledRef.current) {
        setValue(stateKeys.selectedRowKeys, next);
      }
    },
    [getValue, setValue]
  );

  const allSelectHandler = useCallback(
    ({ keys, selected }) => {
      const current = new Set(
        getValue(stateKeys.selectedRowKeys) ?? emptyArray
      );

      keys.forEach((key) => {
        const action = selected ? 'add' : 'delete';
        current[action](key);
      });

      const next = Array.from(current);

      onChangeRef.current({
        value: next,
        valueSet: current,
        selected,
        targetKeysSet: keys,
      });

      if (!isControlledRef.current) {
        setValue(stateKeys.selectedRowKeys, next);
      }
    },
    [setValue, getValue]
  );

  useEffect(() => {
    if (isControlledRef.current) {
      setValue(key, value);
    } else if (defaultValue) {
      setValue(key, defaultValue);
    }
  }, [value, defaultValue, setValue]);

  const handlers = useMemo(
    () => ({
      onSelect: selectHandler,
      onSelectAll: allSelectHandler,
    }),
    [selectHandler, allSelectHandler]
  );

  return <Context.Provider value={handlers}>{children}</Context.Provider>;
};
